import { zodResolver } from '@hookform/resolvers/zod';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { hasReadOnlyPermission } from 'utils/helpers';
import {
  useOperatorsDriverCandidatesQuery,
  usePostOperatorsVehiclesDriversMutation,
} from 'api/queries/operators';
import { ControlledField, SelectInputField, SelectOption } from 'remote/global';
import { Button } from '@mui/material';
import { VehicleDriverSchemaType, vehicleDriverSchema } from './types';

function VehicleAssignDriverFormFields({
  form,
  options,
}: {
  form: UseFormReturn<VehicleDriverSchemaType>;
  options?: SelectOption[];
}) {
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { data: driverCandidates } = useOperatorsDriverCandidatesQuery();
  const { permissions } = userInfo ?? {};
  const { control } = form;
  const isFormDisabled = hasReadOnlyPermission(permissions?.VEHICLE_DETAILS ?? '');

  const getOptions = (): SelectOption[] => {
    if (driverCandidates == null) return [];
    const result =
      driverCandidates.map((c) => ({
        label: c.value,
        value: c.id,
      })) ?? [];
    return result;
  };

  return (
    <ControlledField
      control={control}
      name='defaultDriverId'
      disabled={isFormDisabled}
      render={(renderProps) => (
        <SelectInputField {...renderProps} options={options ?? getOptions()} />
      )}
    />
  );
}

function VehicleAssignDriverForm({
  defaultValues,
  vehicleId,
  options,
}: {
  defaultValues?: VehicleDriverSchemaType;
  vehicleId: string;
  options: SelectOption[];
}) {
  const { t } = useTranslation();
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions } = userInfo ?? {};
  const formInstance = useForm<VehicleDriverSchemaType>({
    defaultValues: {
      defaultDriverId: undefined,
      ...defaultValues,
    },
    resolver: zodResolver(vehicleDriverSchema),
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;
  const isFormDisabled = hasReadOnlyPermission(permissions?.VEHICLE_DETAILS ?? '');
  const postOperatorsVehiclesDriversMutation = usePostOperatorsVehiclesDriversMutation(vehicleId);

  const onValid = (data: VehicleDriverSchemaType) =>
    postOperatorsVehiclesDriversMutation.mutate({
      driverId: String(data.defaultDriverId),
    });

  // TODO: Make it possible to clear this value (set as null) by transforming it into an Autocomplete
  return (
    <form onSubmit={handleSubmit(onValid)}>
      <VehicleAssignDriverFormFields form={formInstance} options={options} />
      <Button
        type='submit'
        variant='contained'
        disabled={isLoading || isSubmitting || isValidating || isFormDisabled}
        fullWidth
        sx={{ mt: '2rem' }}
      >
        {t('save')}
      </Button>
    </form>
  );
}

export default VehicleAssignDriverForm;
